<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick, watch } from 'vue';
import DealerCard from '@/components/cards/DealerCard.vue';
const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
  view: {
    type: String,
    default: () => '',
  },
  backgroundColor: {
    type: String,
    default: () => '',
  },
});

const hoveredCard = ref(null);
const setHoveredCard = (cardId) => {
  hoveredCard.value = cardId;
};

const clearHoveredCard = () => {
  hoveredCard.value = null;
};

const activeTab = ref(
  props.view === 'nav' || props.view === 'nav-mobile'
    ? `nav-${props.data?.[0]?.id}`
    : props.data?.[0]?.id,
);
const tabListElement = useTemplateRef('tabList');

const onTabListResize = () => {
  tabListElement.value?.updateInkBar();
};
onMounted(() => {
  window.addEventListener('resize', onTabListResize, { passive: true });
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', onTabListResize);
});
watch(activeTab, async () => {
  await nextTick();
  window.dispatchEvent(new Event('resize'));
});
</script>
<template>
  <Tabs
    v-model:value="activeTab"
    :scrollable="false"
    class="tabs"
    :class="[`tabs tabs--${view}`]"
  >
    <TabList
      ref="tabList"
      :class="`tab-headers tabs-bg-${backgroundColor}`"
      :data-aos="view === 'nav' || view === 'nav-mobile' ? '' : 'fade-in'"
      :data-aos-delay="view === 'nav' || view === 'nav-mobile' ? 0 : 100"
    >
      <Tab
        v-for="tab in data"
        :key="tab.id"
        :value="
          view === 'nav' || view === 'nav-mobile' ? `nav-${tab.id}` : tab.id
        "
        :class="[`tab-title tab-title--${backgroundColor}`]"
      >
        {{ tab.title }}
      </Tab>
    </TabList>
    <TabPanels class="tabs-content">
      <TabPanel
        v-for="tab in data"
        :key="tab.id"
        :value="
          view === 'nav' || view === 'nav-mobile' ? `nav-${tab.id}` : tab.id
        "
      >
        <div
          v-if="tab.description"
          :class="[
            'dealer__description',
            backgroundColor === 'black' ? 'white' : '',
          ]"
          v-html="tab.description"
        ></div>
        <ul
          class="m-0 p-0 dealer__items"
          :class="{
            'has-hovered-card':
              view !== 'nav' && view !== 'nav-mobile' && hoveredCard !== null,
          }"
        >
          <li
            v-for="(item, itemIndex) in tab.items"
            :key="item.title"
            class="m-0 dealer__item"
            :data-aos="view === 'nav' || view === 'nav-mobile' ? '' : 'fade-up'"
            :data-aos-delay="
              view === 'nav' || view === 'nav-mobile'
                ? 0
                : 100 * (itemIndex + 1)
            "
          >
            <DealerCard
              :view="view"
              :title="item?.title"
              :logo="item?.logo"
              :url="item?.url"
              :background-color="backgroundColor"
              :class="{
                'is-blurred':
                  view !== 'nav' &&
                  view !== 'nav-mobile' &&
                  hoveredCard !== null &&
                  hoveredCard !== `${activeIndex}-${itemIndex}`,
                'is-hovered':
                  view !== 'nav' &&
                  view !== 'nav-mobile' &&
                  hoveredCard === `${activeIndex}-${itemIndex}`,
              }"
              @mouseenter="setHoveredCard(`${activeIndex}-${itemIndex}`)"
              @mouseleave="clearHoveredCard"
            />
          </li>
        </ul>
      </TabPanel>
    </TabPanels>
  </Tabs>
</template>

<style scoped lang="scss">
.tabs {
  display: flex;
  flex-direction: column;
  gap: 40px;
  @include media-breakpoint-up(xl) {
    @include responsive-spacing(gap, 40);
  }
  &-content {
    display: flex;
    flex-direction: column;
    gap: 40px;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 40);
    }
    &:deep(.p-tabpanel) {
      display: flex;
      flex-direction: column;
      gap: 40px;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 40);
      }
    }
  }
  &--nav {
    padding: 24px;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(padding, 24);
    }
    .tab {
      &-title {
        @include link-normal;
        width: 50%;
        padding: 17px 12px 15px;
        @include media-breakpoint-up(md) {
          width: auto;
        }
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(padding-top, 17);
          @include responsive-spacing(padding-left, 12);
          @include responsive-spacing(padding-right, 12);
          @include responsive-spacing(padding-bottom, 15);
        }
      }
      &-headers {
        background-color: $white;
        justify-content: flex-start;
        position: sticky;
        top: 24px;
        &:deep(.p-tablist-tab-list) {
          justify-content: flex-start;
        }
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(top, 24);
        }
      }
    }
    .dealer {
      &__items {
        display: block;
        column-count: 2;
        column-gap: 8px;
      }
      &__item {
        margin-bottom: 8px;
      }
      @include media-breakpoint-up(xl) {
        &__items {
          @include responsive-spacing(column-gap, 8);
        }
        &__item {
          @include responsive-spacing(margin-bottom, 8);
        }
      }
    }
  }
  &--nav-mobile {
    gap: 24px;
    @include media-breakpoint-up(sm) {
      gap: 40px;
    }
    .tab {
      &-title {
        @include link-normal;
        color: $on-surface-variant;
        padding: 13px 16px 16px;
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(padding-bottom, 16);
          @include responsive-spacing(padding-top, 13);
          @include responsive-spacing(padding-left, 16);
          @include responsive-spacing(padding-right, 16);
        }
        &[data-p='active'] {
          color: $white;
        }
      }
      &-headers {
        justify-content: flex-start;
        &:deep(.p-tablist-tab-list) {
          justify-content: flex-start;
        }
      }
    }
    .dealer {
      &__items {
        display: block;
        column-count: 2;
        column-gap: 8px;
      }
      &__item {
        margin-bottom: 8px;
      }
      @include media-breakpoint-up(xl) {
        &__items {
          @include responsive-spacing(column-gap, 8);
        }
        &__item {
          @include responsive-spacing(margin-bottom, 8);
        }
      }
    }
  }
}
.tab {
  &-headers {
    display: flex;
    justify-content: center;
    position: relative;
    &:deep(.p-tablist-tab-list) {
      justify-content: center;
    }
  }
  &-title {
    @include h5;
    border-bottom: 1px solid $divider;
    cursor: pointer;
    position: relative;
    padding: 0 12px 14px;
    text-align: center;
    transition: color 0.2s ease;
    white-space: unset;
    width: 50%;
    &[data-p='active'] {
      color: $on-surface;
    }
    &--white,
    &--gray {
      color: $on-surface-variant-2;
    }
    &--black {
      color: $on-surface-variant-2;
      &[data-p='active'] {
        color: $white;
      }
    }
    @include media-breakpoint-up(sm) {
      padding: 17px 16px 15px;
      width: auto;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-bottom-width, 1);
      @include responsive-spacing(padding-top, 17);
      @include responsive-spacing(padding-right, 16);
      @include responsive-spacing(padding-left, 16);
      @include responsive-spacing(padding-bottom, 15);
    }
  }
}
.dealer {
  &__description {
    @include body-3-regular;
    text-align: center;
    @include media-breakpoint-up(sm) {
      max-width: 50%;
      margin: 0 auto;
    }
  }

  &__items {
    display: grid;
    gap: 8px;
    grid-template-columns: repeat(2, 2fr);
    list-style: none;
    @include media-breakpoint-up(sm) {
      grid-template-columns: repeat(4, 3fr);
    }
    @include media-breakpoint-up(md) {
      grid-template-columns: repeat(6, 2fr);
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 8);
    }
    &.has-hovered-card {
      @include media-breakpoint-up(md) {
        :deep(.dealer) {
          transition: all 0.3s ease;
          &.is-blurred {
            opacity: 0.5;
          }
          &.is-hovered {
            opacity: 1;
          }
        }
      }
    }
  }
}
</style>
